import type { PublicAnalytics, PublicEquity } from "../../contracts/src/index.ts";

const DASHBOARD_CACHE_VERSION = 1;
const DASHBOARD_DATA_VERSION = "v5.4";
const ANALYTICS_CACHE_PREFIX = "hq:last-analytics";
const EQUITY_CACHE_PREFIX = "hq:last-equity-overview";

interface CachedPublicDto<T> {
  schemaVersion: typeof DASHBOARD_CACHE_VERSION;
  dataVersion: typeof DASHBOARD_DATA_VERSION;
  value: T;
}

function scopedKey(
  prefix: string,
  apiBaseUrl: string,
  accountId: string,
  variant: string,
): string {
  const scope = `${apiBaseUrl.replace(/\/$/, "")}|${accountId}|${variant}`;
  return `${prefix}:v${DASHBOARD_CACHE_VERSION}:${encodeURIComponent(scope)}`;
}

function isAnalytics(value: unknown, accountId: string): value is PublicAnalytics {
  if (!value || typeof value !== "object") return false;
  const analytics = value as Partial<PublicAnalytics>;
  return analytics.accountId === accountId && analytics.dataVersion === DASHBOARD_DATA_VERSION &&
    typeof analytics.generatedAt === "number" && Array.isArray(analytics.monthly) &&
    Array.isArray(analytics.availableStrategies) && Boolean(analytics.scope);
}

function isEquity(value: unknown, accountId: string): value is PublicEquity {
  if (!value || typeof value !== "object") return false;
  const equity = value as Partial<PublicEquity>;
  return equity.accountId === accountId && Boolean(equity.historical_balance) &&
    Boolean(equity.live_balance) && Boolean(equity.live_equity) && Array.isArray(equity.points) &&
    Array.isArray(equity.historical_balance?.points) &&
    Array.isArray(equity.live_balance?.points) && Array.isArray(equity.live_equity?.points);
}

function readCached<T>(
  storage: Pick<Storage, "getItem">,
  key: string,
  validate: (value: unknown) => value is T,
): T | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const cached = JSON.parse(raw) as Partial<CachedPublicDto<unknown>>;
    if (
      cached.schemaVersion !== DASHBOARD_CACHE_VERSION ||
      cached.dataVersion !== DASHBOARD_DATA_VERSION ||
      !validate(cached.value)
    ) {
      return null;
    }
    return cached.value;
  } catch {
    return null;
  }
}

function writeCached<T>(
  storage: Pick<Storage, "setItem">,
  key: string,
  value: T,
): void {
  try {
    const cached: CachedPublicDto<T> = {
      schemaVersion: DASHBOARD_CACHE_VERSION,
      dataVersion: DASHBOARD_DATA_VERSION,
      value,
    };
    storage.setItem(key, JSON.stringify(cached));
  } catch {
    // Live refresh remains authoritative when storage is unavailable or full.
  }
}

export function readLastKnownGoodAnalytics(
  storage: Pick<Storage, "getItem">,
  apiBaseUrl: string,
  accountId: string,
  strategy: string,
): PublicAnalytics | null {
  return readCached(
    storage,
    scopedKey(ANALYTICS_CACHE_PREFIX, apiBaseUrl, accountId, strategy),
    (value): value is PublicAnalytics => isAnalytics(value, accountId),
  );
}

export function writeLastKnownGoodAnalytics(
  storage: Pick<Storage, "setItem">,
  apiBaseUrl: string,
  accountId: string,
  strategy: string,
  analytics: PublicAnalytics,
): void {
  if (!isAnalytics(analytics, accountId)) return;
  writeCached(
    storage,
    scopedKey(ANALYTICS_CACHE_PREFIX, apiBaseUrl, accountId, strategy),
    analytics,
  );
}

export function readLastKnownGoodEquityOverview(
  storage: Pick<Storage, "getItem">,
  apiBaseUrl: string,
  accountId: string,
  period: string,
): PublicEquity | null {
  return readCached(
    storage,
    scopedKey(EQUITY_CACHE_PREFIX, apiBaseUrl, accountId, period),
    (value): value is PublicEquity => isEquity(value, accountId),
  );
}

export function writeLastKnownGoodEquityOverview(
  storage: Pick<Storage, "setItem">,
  apiBaseUrl: string,
  accountId: string,
  period: string,
  equity: PublicEquity,
): void {
  if (!isEquity(equity, accountId)) return;
  writeCached(
    storage,
    scopedKey(EQUITY_CACHE_PREFIX, apiBaseUrl, accountId, period),
    equity,
  );
}
